import { I18nNamespace } from 'constants/i18n.const';
import {
  SHOOTING_TYPES,
  TIME_DELAY_PROCESS_VIDEO,
  TIME_TO_DELAY_REDIRECT_PRINT,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import {
  calculateCoordinatesAfterRotation,
  calculateSizeAfterRotation,
} from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import { diffFromDate } from 'helpers/time.helper';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { delay, filter, map } from 'lodash';
import { SelfeAppStateModel } from 'models/app.model';
import { LayoutPhotoModel } from 'models/photo/layout.model';
import { TrackingStateModel } from 'models/photo/tracking.model';
import { PaymentMethodEnum } from 'models/photo/transaction.model';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useProcessPhotoTakenApiActionMutation,
  useProcessVideoTakenApiActionMutation,
} from 'store/api/photo-taken.slice-api';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useModal } from '../../../hooks/useModal';
import { useCheckPayment } from '../payment/useCheckPayment';
import { processPickedPhotos } from './edit-photo.helper';

export function useEditPhotoActions() {
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const { audio } = useSoundContext();
  const navigate = useNavigate();
  const { getPrevPath, getNextPath } = useNewPhotoLifeCycleStep();
  const dispatch = useAppDispatch();
  const [processPhotoTaken] = useProcessPhotoTakenApiActionMutation();
  const [processVideoTaken] = useProcessVideoTakenApiActionMutation();
  const { openConfirmModal, closeConfirmModal } = useModal();
  const { costOfPayment } = useCheckPayment();

  const {
    frameId,
    layoutId,
    themeId,
    themeDetailId,
    depositAmount,
    quantitySheet,
    appliedVoucherCode,
    appliedVoucherValue,
    shootingModeId,
    filterId,
    transactionId,
    pickedPhotos,
    listSelectedSticker,
    photoContainer,
    backgroundId,
    isChooseQRCodeToPay,
    paymentMethodForTest,
    filterType,
    pinCode,
    isChooseVNPayToPay,
    isConfirmPolicy,
  } = useAppSelector(
    (state: { selfeApp: SelfeAppStateModel }) => state.selfeApp,
  );
  const { currentLayout } = useBoothAppContext();
  const {
    LastStartTimePurchase,
    LastStartTimeTakePhoto,
    LastStartTimeEditPhoto,
  } = useAppSelector(
    (state: { tracking: TrackingStateModel }) => state.tracking,
  );

  const handleBack = useCallback(() => {
    audio?.playBackClick?.();
    navigate(getPrevPath() || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPrevPath, navigate]);

  const handleNext = () => {
    openConfirmModal({
      content: `${t('common:printImageConfirm')}`,
      PrevButtonProps: { children: t('common:cancel') },
      NextButtonProps: { children: t('common:continue') },
      duration: 30000,
      isUseTimer: true,
      isTimerInBtnNext: true,
      onOk: () => {
        closeConfirmModal?.();
        handleProcessPrintImage();
      },
      onTimeUp: () => {
        closeConfirmModal();
        handleProcessPrintImage();
      },
      onClose: () => {
        closeConfirmModal();
      },
    });
  };

  const handleProcessPrintImage = useCallback(async () => {
    audio?.playContinueClick?.();
    const isHasVideo =
      !shootingModeId || isEqualVal(shootingModeId, SHOOTING_TYPES.COUNTDOWN);
    const layoutAmount =
      +(currentLayout as LayoutPhotoModel)?.prices?.[0]?.price || 0;
    const listStickerFormData = map(
      filter(listSelectedSticker, (i) => i.width > 0 && i.height > 0),
      (item: {
        stickerId: any;
        rotate: any;
        width: any;
        height: any;
        translate: any[];
      }) => {
        const newSize = calculateSizeAfterRotation(
          item.width,
          item.height,
          item.rotate,
        );
        const newCoordinates = calculateCoordinatesAfterRotation(
          item.translate[0],
          item.translate[1],
          item.width,
          item.height,
          newSize.width,
          newSize.height,
        );
        return {
          stickerId: item.stickerId,
          rotate: Math.round(item.rotate),
          width: Math.round(
            (newSize.width / (photoContainer?.clientWidth || 0)) *
              (currentLayout?.width || 0),
          ),
          height: Math.round(
            (newSize.height / (photoContainer?.clientHeight || 0)) *
              (currentLayout?.height || 0),
          ),
          axisX: Math.round(
            (Math.round(newCoordinates.x) /
              (photoContainer?.clientWidth || 0)) *
              (currentLayout?.width || 0),
          ),
          axisY: Math.round(
            (Math.round(newCoordinates.y) /
              (photoContainer?.clientHeight || 0)) *
              (currentLayout?.height || 0),
          ),
        };
      },
    );
    dispatch(
      setSelfeAppStateAction({ appLoading: true, isOpenPlaylist: true }),
    );

    // payload cho api xử lý ảnh video
    const processFilePayload = {
      frameId,
      layoutId,
      themeId,
      themeDetailId,
      transactionId,
      filterId,
      listImages: processPickedPhotos(pickedPhotos, currentLayout, {
        backgroundId,
        filterId,
        filterType,
      }),
      isFile: true,
      isVideo: isHasVideo,
      voucherCode: appliedVoucherCode,
      purchaseDuration: diffFromDate(
        LastStartTimePurchase,
        LastStartTimeTakePhoto,
      ),
      captureDuration: diffFromDate(
        LastStartTimeTakePhoto,
        LastStartTimeEditPhoto,
      ),
      editDuration: diffFromDate(LastStartTimeEditPhoto, new Date()),
      captureMode: shootingModeId,
      printNumber: quantitySheet,
      layoutAmount,
      printAmount: costOfPayment - layoutAmount,
      discount: appliedVoucherValue,
      deposit: depositAmount - appliedVoucherValue,
      listSticker: listStickerFormData,
      isDigitalBackground: !!backgroundId,
      digitalBackgroundId: backgroundId,
      isConfirmPolicy,
      paymentMethod: isChooseQRCodeToPay
        ? PaymentMethodEnum.MOMO
        : PaymentMethodEnum.CASH,
      ...(isChooseVNPayToPay && {
        paymentMethod: PaymentMethodEnum.VNPAY,
      }),
      // Nếu trong chế độ test thử
      ...(paymentMethodForTest > 0 && {
        layoutAmount: 0,
        printAmount: 0,
        paymentMethod: paymentMethodForTest,
        pinCode,
      }),
    };
    // process ảnh
    await processPhotoTaken(processFilePayload);
    await delay(
      () => processVideoTaken(processFilePayload),
      TIME_DELAY_PROCESS_VIDEO,
    );

    delay(() => {
      navigate(getNextPath() || '');
      dispatch(
        setSelfeAppStateAction({ appLoading: false, isOpenPlaylist: false }),
      );
    }, TIME_TO_DELAY_REDIRECT_PRINT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    LastStartTimeEditPhoto,
    LastStartTimePurchase,
    LastStartTimeTakePhoto,
    backgroundId,
    dispatch,
    filterId,
    filterType,
    listSelectedSticker,
    photoContainer?.clientHeight,
    photoContainer?.clientWidth,
    processPhotoTaken,
    processVideoTaken,
    themeId,
    themeDetailId,
    frameId,
    layoutId,
    transactionId,
    shootingModeId,
    currentLayout,
    pickedPhotos,
    quantitySheet,
    appliedVoucherCode,
    appliedVoucherValue,
    depositAmount,
    costOfPayment,
    isChooseQRCodeToPay,
    paymentMethodForTest,
    pinCode,
    isConfirmPolicy,
  ]);

  return {
    handleBack,
    handleNext,
    handleProcessPrintImage,
  };
}
